import React, { useEffect } from 'react';
import { ModalProps } from '@/Types';
import { useGenerateDocumentNumbersTexts } from './hooks/useGenerateDocumentNumbersTexts';
import { useLocalization } from '@/Hooks';
import sharedStyles from '@/ModalWindows/ModalWindows.scss';
import { Button } from '@/Components/Controls/Button/Button';
import { BUTTON_THEMES } from '@/Components/Controls/Button/ButtonThemes';
import { Modal } from '@/Components/Modal/Modal';
import { Label } from '@/Components/Controls/Label/Label';
import { FormCheckbox } from '@/Components/Controls/Checkbox/FormCheckbox';
import { useSelector } from 'react-redux';
import { generateDocumentNumberModalSelectors } from './services/selectors';
import { useGenerateDocumentNumbersOptions } from './hooks/useGenerateDocumentNumbersOptions';
import { FormInput } from '@/Components/Controls/Input/FormInput';
import { stringUtils } from '@/Utils';
import { FormData, useGenerateDocumentNumbersForm } from './hooks/useGenerateDocumentNumbersForm';
import { ButtonsWrapper } from '@/Components/Forms/ButtonsSection/ButtonsSection';
import styles from './GenerateDocumenNumberModal.scss';

type Props = ModalProps;

export function GenerateDocumentNumberModal({ closeModalWindow }: Props) {
  const {
    buttons: { Yes, No },
    modalWindows: { generateDocumentNumbers },
  } = useLocalization();

  const numberGenerationType = useSelector(generateDocumentNumberModalSelectors.numberGenerateType);

  const {
    onSubmit,
    onIsSortChange,
    onCountCopyChange,
    onPrintChange,
    onSendEmailChange,
    methods: { register, setValue, watch },
  } = useGenerateDocumentNumbersForm(numberGenerationType);

  const countCopy = watch('countCopy');

  const { confirmationText } = useGenerateDocumentNumbersTexts(numberGenerationType);
  const { selectedPrintTemplate, selectedEmailTemplate } = useGenerateDocumentNumbersOptions(numberGenerationType);

  useEffect(() => {
    if (!countCopy) {
      setValue('countCopy', selectedPrintTemplate?.generateNumberCountCopy);
      setValue('isSortWhenPrinting', selectedPrintTemplate?.isSortWhenPrinting);
    }

    setValue('printDocument', selectedPrintTemplate?.generateNumberPrintDocument);
    setValue('sendOrderEmail', selectedEmailTemplate?.generateNumberSendOrderEmail);
  }, [selectedPrintTemplate, selectedEmailTemplate]);

  return (
    <Modal
      onClose={closeModalWindow}
      className={sharedStyles.modalWrapper}
      tourKey={'documentCreatingTour'}
      modalId={'generateNumberModal'}
      description={{
        title: generateDocumentNumbers.ModalWindowTitle,
      }}
    >
      <p dangerouslySetInnerHTML={{ __html: confirmationText }} />

      <form onSubmit={onSubmit}>
        <div>
          <div className={styles.modalActions}>
            <div className={styles.printWrapper}>
              <Label
                text={stringUtils.insertParamsToString(generateDocumentNumbers.PrintDocument, [
                  selectedPrintTemplate?.name,
                ])}
                reverse
              >
                <FormCheckbox<FormData>
                  name={'printDocument'}
                  register={register}
                  disabled={!selectedPrintTemplate}
                  onChange={onPrintChange(selectedPrintTemplate!)}
                />
              </Label>

              {selectedPrintTemplate && (
                <>
                  <FormInput<FormData>
                    register={register}
                    name={'countCopy'}
                    type={'number'}
                    min={1}
                    max={10}
                    onChange={onCountCopyChange(selectedPrintTemplate)}
                  />

                  {countCopy != 1 && (
                    <Label text={generateDocumentNumbers.Sorting} reverse>
                      <FormCheckbox<FormData>
                        name={'isSortWhenPrinting'}
                        register={register}
                        onChange={onIsSortChange(selectedPrintTemplate)}
                      />
                    </Label>
                  )}
                </>
              )}
            </div>
            {!selectedPrintTemplate && (
              <div className={styles.validationMessage}>{generateDocumentNumbers.TemplateNotExists}</div>
            )}
          </div>

          <div className={styles.modalActions}>
            <Label
              text={stringUtils.insertParamsToString(generateDocumentNumbers.SendEmail, [selectedEmailTemplate?.name])}
              reverse
            >
              <FormCheckbox<FormData>
                name={'sendOrderEmail'}
                register={register}
                disabled={!selectedEmailTemplate}
                onChange={onSendEmailChange(selectedEmailTemplate!)}
              />
            </Label>
            {!selectedEmailTemplate && (
              <div className={styles.validationMessage}>{generateDocumentNumbers.TemplateNotExists}</div>
            )}
          </div>
        </div>

        <ButtonsWrapper>
          <Button theme={BUTTON_THEMES.SUCCESS_TRANSPARENT} text={No} onClick={closeModalWindow} />
          <Button type={'submit'} autoFocus={true} theme={BUTTON_THEMES.SUCCESS} text={Yes} />
        </ButtonsWrapper>
      </form>
    </Modal>
  );
}
